import React, { useCallback, useEffect, useState, useRef } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  Flex,
  Text,
  Alert,
  AlertIcon,
  Button,
  Icon,
  useToast,
  IconButton,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useLocation, useHistory } from "react-router-dom";
import { useFormik } from "formik";
import { useStores } from "../../../stores/setup/use-store";
import { IOperationRule } from "../../../stores/data/operationRules/OperationRuleModel";
import { StoreState } from "../../../enum/StoreState";
import Loading from "../../../components/Loading/Loading";
import EmptyState from "../../../components/EmptyState/EmptyState";
import noInsights from "../../../assets/noInsights.svg";
import { useEventManagerData } from "./hooks/useEventManagerData";
import LocationAccordion from "./LocationAccordion";
import i18n from "../../../i18n";
import {
  ILocationMenuItem,
  ILocationMenuInsight,
} from "../../../stores/data/operationRuleLocations/OperationRuleLocationModel";
import { ArrowDownUp, Plus, Check, X, Pencil, Trash2 } from "lucide-react";
import { EventCreateDrawer, EventEditDrawer } from "pages/Event";
import { useAlertPhoneVerification } from "pages/Alert/hooks/useAlertPhoneVerification";
import { PhoneVerificationModal } from "pages/Admin/MyData/components/PhoneVerificationModal";
import { CreateDashboardModal } from "pages/Event/components/CreateDashboardModal";
import DeleteConfirmModal from "./components/DeleteConfirmModal";
import DeleteDashboardModal from "./components/DeleteDashboardModal";
import { LocationEditDrawer } from "./LocationEditDrawer";
import DashboardSelect from "../components/DashboardSelect";
import { DashboardModal } from "../components/DashboardModal";

interface EventManagerTabProps {}

const EventManagerTab: React.FC<EventManagerTabProps> = observer(() => {
  const {
    dataStores: {
      operationRuleStore,
      authStore,
      operationRuleLocationInsightStore,
      operationRuleLocationStore,
      oceanWeatherIntelligenceStore,
      insightAlertStore,
    },
  } = useStores();

  const { t } = useTranslation("translation", {
    keyPrefix: "oceanWeatherIntelligence.insightManager.eventManager",
  });

  const toast = useToast();

  const {
    selectedDashboardId,
    setSelectedDashboardId,
    locationMenuList,
    isLoading,
    error,
    updateEventsForLocation,
    updateLocationOrder,
    refreshData,
    removeLocation,
    removeEvent,
  } = useEventManagerData();

  // Event Create Drawer state
  const {
    isOpen: isEventDrawerOpen,
    onOpen: onEventDrawerOpen,
    onClose: onEventDrawerClose,
  } = useDisclosure();
  const [drawerInitialStep, setDrawerInitialStep] = useState(0);

  // Phone verification hook
  const {
    isOpen: isPhoneModalOpen,
    onOpen: onPhoneModalOpen,
    onClose: onPhoneModalClose,
    phoneData,
    handlePhoneUpdateSuccess,
    isPhoneAlreadyVerified,
    t: tPhoneModal,
  } = useAlertPhoneVerification();

  // Delete modal state
  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onClose: onDeleteModalClose,
  } = useDisclosure();
  const [deleteItem, setDeleteItem] =
    useState<{
      id: number;
      name: string;
      type: "location" | "event";
      locationId?: number;
    } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Event Edit Drawer state
  const {
    isOpen: isEditDrawerOpen,
    onOpen: onEditDrawerOpen,
    onClose: onEditDrawerClose,
  } = useDisclosure();
  const [eventToEdit, setEventToEdit] =
    useState<ILocationMenuInsight | null>(null);
  const [editEventLocationId, setEditEventLocationId] =
    useState<number | null>(null);
  const [editEventLocationName, setEditEventLocationName] =
    useState<string>("");
  const [isReturningFromSensorSelection, setIsReturningFromSensorSelection] =
    useState(false);

  // State for creating event from a specific location
  const [prefilledLocation, setPrefilledLocation] =
    useState<ILocationMenuItem | null>(null);

  // Location Edit Drawer state
  const {
    isOpen: isLocationEditDrawerOpen,
    onOpen: onLocationEditDrawerOpen,
    onClose: onLocationEditDrawerClose,
  } = useDisclosure();
  const [locationToEdit, setLocationToEdit] =
    useState<ILocationMenuItem | null>(null);
  const [isLocationUpdating, setIsLocationUpdating] = useState(false);

  // Create Dashboard Modal state
  const {
    isOpen: isCreateDashboardOpen,
    onOpen: onCreateDashboardOpen,
    onClose: onCreateDashboardClose,
  } = useDisclosure();

  // Edit Dashboard Modal state
  const {
    isOpen: isEditDashboardOpen,
    onOpen: onEditDashboardOpen,
    onClose: onEditDashboardClose,
  } = useDisclosure();
  const [dashboardToEdit, setDashboardToEdit] =
    useState<IOperationRule | null>(null);
  const [isDashboardUpdating, setIsDashboardUpdating] = useState(false);

  // Delete Dashboard Modal state
  const {
    isOpen: isDeleteDashboardOpen,
    onOpen: onDeleteDashboardOpen,
    onClose: onDeleteDashboardClose,
  } = useDisclosure();
  const [dashboardToDelete, setDashboardToDelete] =
    useState<IOperationRule | null>(null);
  const [isDashboardDeleting, setIsDashboardDeleting] = useState(false);

  // Pin loading state
  const [pinLoadingId, setPinLoadingId] = useState<number | null>(null);

  // Ref for edit modal input
  const editDashboardInputRef = useRef<HTMLInputElement>(null);

  // Formik for edit dashboard
  const formikEditDashboard = useFormik({
    initialValues: {
      editDashboardName: "",
    },
    onSubmit: () => {},
  });

  // Router hooks for URL parameter handling
  const location = useLocation();
  const history = useHistory();

  // Effect to open drawer when returning from map selection
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const openCreateDrawer = queryParams.get("open_create_drawer");

    if (openCreateDrawer === "true") {
      setDrawerInitialStep(1); // Step 2 (0-indexed) — returning from sensor selection
      onEventDrawerOpen();
      // Clean up URL parameter after opening drawer
      queryParams.delete("open_create_drawer");
      const newSearch = queryParams.toString();
      history.replace({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : "",
      });
    }
  }, [location.search, onEventDrawerOpen, history, location.pathname]);

  // Effect to reopen location edit drawer when returning from Forecast Charts
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const editEventLocationId = queryParams.get("edit_event_location_id");

    if (editEventLocationId) {
      const locationIdNumber = parseInt(editEventLocationId, 10);

      // Find the location in the current list
      const locationFound = locationMenuList.find(
        (loc) => loc.id === locationIdNumber,
      );

      if (locationFound) {
        // The store will have the new coordinates from DrawMapControls or PointSelectionMapControls
        // The LocationEditDrawer will use oceanWeatherIntelligenceStore.selectedGeofence directly

        // Set the location to edit and open the drawer
        setLocationToEdit(locationFound);
        onLocationEditDrawerOpen();

        // Clean up URL parameter after opening drawer
        queryParams.delete("edit_event_location_id");
        const newSearch = queryParams.toString();
        history.replace({
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : "",
        });
      }
    }
  }, [
    location.search,
    locationMenuList,
    onLocationEditDrawerOpen,
    history,
    location.pathname,
  ]);

  // State for location reordering mode
  const [isLocationDragMode, setIsLocationDragMode] = useState(false);
  const [pendingLocationOrder, setPendingLocationOrder] =
    useState<ILocationMenuItem[] | null>(null);
  const [locationSaveKey, setLocationSaveKey] = useState(0);

  // Get selected dashboard name
  const selectedDashboard = operationRuleStore.operationRules.find(
    (d) => d.id === selectedDashboardId,
  );

  // Combined loading state: dashboards loading OR events/locations loading
  const isDashboardsLoading = operationRuleStore.state === StoreState.PEDDING;
  const isCombinedLoading = isDashboardsLoading || isLoading;

  // Handle dashboard selection change
  const handleSelectDashboard = (dashboardId: number) => {
    // Don't do anything if the same dashboard is already selected
    if (dashboardId === selectedDashboardId) {
      return;
    }
    if (dashboardId > 0) {
      setSelectedDashboardId(dashboardId);
      // Sync with store so operation-insights page shows correct data
      operationRuleStore.setOperationRuleMain(dashboardId);
      // Signal that Gantt chart needs to update
      oceanWeatherIntelligenceStore.setChangeForUpdate({
        update: true,
        expiration: 0,
      });
    }
  };

  // Handle dashboard creation callback
  const handleDashboardCreated = useCallback(
    (newDashboardId: number) => {
      // Set the newly created dashboard as selected
      setSelectedDashboardId(newDashboardId);
      operationRuleStore.setOperationRuleMain(newDashboardId);
      // Signal that Gantt chart needs to update
      oceanWeatherIntelligenceStore.setChangeForUpdate({
        update: true,
        expiration: 0,
      });
      // Close the modal
      onCreateDashboardClose();
    },
    [
      setSelectedDashboardId,
      operationRuleStore,
      oceanWeatherIntelligenceStore,
      onCreateDashboardClose,
    ],
  );

  // Handle pin toggle for dashboard
  const handlePinToggle = useCallback(
    async (item: IOperationRule) => {
      setPinLoadingId(item.id);
      try {
        await operationRuleStore.addOperationRuleFavorite(item.id);

        // Check store state after update - store doesn't throw on API errors
        if (operationRuleStore.state === "error") {
          toast({
            title:
              operationRuleStore.messageError || t("dashboardFavoriteError"),
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          return;
        }

        toast({
          title: item.isFavorite
            ? t("dashboardUnfavoriteSuccess")
            : t("dashboardFavoriteSuccess"),
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        console.error("Error toggling favorite:", error);
        toast({
          title: t("dashboardFavoriteError"),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setPinLoadingId(null);
      }
    },
    [operationRuleStore, toast, t],
  );

  // Handle edit dashboard click
  const handleEditDashboard = useCallback(
    (item: IOperationRule) => {
      setDashboardToEdit(item);
      formikEditDashboard.setFieldValue("editDashboardName", item.name);
      onEditDashboardOpen();
    },
    [formikEditDashboard, onEditDashboardOpen],
  );

  // Handle save edit dashboard
  const handleSaveEditDashboard = useCallback(
    async (values: { editDashboardName: string }) => {
      if (!dashboardToEdit) return;

      setIsDashboardUpdating(true);
      try {
        await operationRuleStore.updateOperationRuleDashboard(
          dashboardToEdit.id,
          values.editDashboardName,
        );

        // Check store state after update - store doesn't throw on API errors
        if (operationRuleStore.state === "error") {
          toast({
            title: operationRuleStore.messageError || t("dashboardUpdateError"),
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          return;
        }

        toast({
          title: t("dashboardUpdateSuccess"),
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onEditDashboardClose();
        setDashboardToEdit(null);
      } catch (error) {
        console.error("Error updating dashboard:", error);
        toast({
          title: t("dashboardUpdateError"),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsDashboardUpdating(false);
      }
    },
    [dashboardToEdit, operationRuleStore, toast, t, onEditDashboardClose],
  );

  // Handle delete dashboard click
  const handleDeleteDashboard = useCallback(
    (item: IOperationRule) => {
      setDashboardToDelete(item);
      onDeleteDashboardOpen();
    },
    [onDeleteDashboardOpen],
  );

  // Handle confirm delete dashboard
  const handleConfirmDeleteDashboard = useCallback(async () => {
    if (!dashboardToDelete) return;

    setIsDashboardDeleting(true);
    try {
      const success = await operationRuleStore.deleteOperationRuleDashboard(
        dashboardToDelete.id,
      );

      // Check return value - store doesn't throw on API errors
      if (!success || operationRuleStore.state === "error") {
        toast({
          title: operationRuleStore.messageError || t("dashboardDeleteError"),
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Only select another dashboard if we deleted the currently selected one
      if (dashboardToDelete.id === selectedDashboardId) {
        const remainingDashboards = operationRuleStore.operationRules.filter(
          (d) => d.id !== dashboardToDelete.id,
        );
        if (remainingDashboards.length > 0) {
          // Select the first remaining dashboard (or favorite if exists)
          const favorite = remainingDashboards.find((d) => d.isFavorite);
          const nextDashboard = favorite || remainingDashboards[0];
          setSelectedDashboardId(nextDashboard.id);
          operationRuleStore.setOperationRuleMain(nextDashboard.id);
          oceanWeatherIntelligenceStore.setChangeForUpdate({
            update: true,
            expiration: 0,
          });
        } else {
          // No dashboards remaining - clear selection
          setSelectedDashboardId(0);
          operationRuleStore.setOperationRuleMain(0);
        }
      }

      toast({
        title: t("dashboardDeleteSuccess"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onDeleteDashboardClose();
      setDashboardToDelete(null);
    } catch (error) {
      console.error("Error deleting dashboard:", error);
      toast({
        title: t("dashboardDeleteError"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsDashboardDeleting(false);
    }
  }, [
    dashboardToDelete,
    selectedDashboardId,
    operationRuleStore,
    oceanWeatherIntelligenceStore,
    setSelectedDashboardId,
    toast,
    t,
    onDeleteDashboardClose,
  ]);

  // Handle cancel delete dashboard
  const handleCancelDeleteDashboard = useCallback(() => {
    onDeleteDashboardClose();
    setDashboardToDelete(null);
  }, [onDeleteDashboardClose]);

  // Handle event reorder - optimistic update
  const handleEventReorder = useCallback(
    async (locationId: number, newOrder: ILocationMenuInsight[]) => {
      // Store old order for rollback in case of error
      const oldOrder =
        locationMenuList.find((loc) => loc.id === locationId)?.items || [];

      // Optimistic update - update UI immediately
      updateEventsForLocation(locationId, newOrder);

      try {
        const idsInOrder = newOrder.map((event) => event.id!);

        await operationRuleLocationInsightStore.updateOperationRuleLocationItemOrder(
          {
            locationId,
            idsInOrder,
          },
        );

        // Update gantt chart
        oceanWeatherIntelligenceStore.setChangeForUpdate({
          update: true,
          expiration: 0,
        });

        toast({
          title: t("eventReorderSuccess"),
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        console.error("Error reordering events:", error);

        // Rollback to old order on error
        updateEventsForLocation(locationId, oldOrder);

        toast({
          title: t("eventReorderError"),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    },
    [
      operationRuleLocationInsightStore,
      oceanWeatherIntelligenceStore,
      toast,
      t,
      updateEventsForLocation,
      locationMenuList,
    ],
  );

  // Toggle location drag mode
  const handleToggleLocationDragMode = useCallback(() => {
    if (authStore.readOnlyUser) return;
    setIsLocationDragMode((prev) => !prev);
    setPendingLocationOrder(null);
  }, [authStore.readOnlyUser]);

  // Handle location order change from LocationAccordion
  const handleLocationOrderChange = useCallback(
    (newOrder: ILocationMenuItem[]) => {
      setPendingLocationOrder(newOrder);
    },
    [],
  );

  // Save location order - optimistic update
  const handleSaveLocationOrder = useCallback(async () => {
    if (!pendingLocationOrder) return;

    // Store old order for rollback in case of error
    const oldOrder = [...locationMenuList];

    // Optimistic update - update UI immediately
    updateLocationOrder(pendingLocationOrder);

    // Increment save key to prevent flickering
    setLocationSaveKey((prev) => prev + 1);

    // Exit drag mode immediately
    setIsLocationDragMode(false);
    setPendingLocationOrder(null);

    try {
      const idsInOrder = pendingLocationOrder.map((loc) => loc.id);

      await operationRuleLocationStore.updateOperationRuleLocationOrder({
        dashboardId: selectedDashboardId,
        idsInOrder,
      });

      // Update gantt chart
      oceanWeatherIntelligenceStore.setChangeForUpdate({
        update: true,
        expiration: 0,
      });

      toast({
        title: t("locationReorderSuccess"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error reordering locations:", error);

      // Rollback to old order on error
      updateLocationOrder(oldOrder);

      toast({
        title: t("locationReorderError"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [
    pendingLocationOrder,
    selectedDashboardId,
    operationRuleLocationStore,
    oceanWeatherIntelligenceStore,
    updateLocationOrder,
    locationMenuList,
    toast,
    t,
  ]);

  // Cancel location order
  const handleCancelLocationOrder = useCallback(() => {
    setIsLocationDragMode(false);
    setPendingLocationOrder(null);
  }, []);

  // Handle event created - refresh data only on successful creation
  const handleEventCreated = useCallback(() => {
    refreshData();
  }, [refreshData]);

  // Handle drawer close - clear prefilled location
  const handleEventDrawerClose = useCallback(() => {
    onEventDrawerClose();
    setPrefilledLocation(null);
  }, [onEventDrawerClose]);

  // Handle create event for a specific location (from accordion)
  const handleCreateEventForLocation = useCallback(
    (location: ILocationMenuItem) => {
      setPrefilledLocation(location);
      onEventDrawerOpen();
    },
    [onEventDrawerOpen],
  );

  // Handle delete location request
  const handleDeleteLocation = useCallback(
    (locationId: number, locationName: string) => {
      setDeleteItem({
        id: locationId,
        name: locationName,
        type: "location",
      });
      onDeleteModalOpen();
    },
    [onDeleteModalOpen],
  );

  // Handle delete event request
  const handleDeleteEvent = useCallback(
    (eventId: number, eventName: string, locationId: number) => {
      setDeleteItem({
        id: eventId,
        name: eventName,
        type: "event",
        locationId,
      });
      onDeleteModalOpen();
    },
    [onDeleteModalOpen],
  );

  // Handle edit event request
  const handleEditEvent = useCallback(
    (event: ILocationMenuInsight, locationId: number, locationName: string) => {
      setEventToEdit(event);
      setEditEventLocationId(locationId);
      setEditEventLocationName(locationName);
      onEditDrawerOpen();
    },
    [onEditDrawerOpen],
  );

  // Effect to reopen event edit drawer when returning from sensor selection on Forecast Charts
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const openEditDrawer = queryParams.get("open_edit_drawer");
    const eventInsightIdStr = queryParams.get("event_insight_id");

    if (openEditDrawer === "true" && eventInsightIdStr && locationMenuList.length > 0) {
      const eventInsightId = parseInt(eventInsightIdStr, 10);

      // Find the event across all locations
      let foundEvent: ILocationMenuInsight | null = null;
      let foundLocationId: number | null = null;
      let foundLocationName = "";

      for (const loc of locationMenuList) {
        const event = loc.items?.find((item) => item.id === eventInsightId);
        if (event) {
          foundEvent = event;
          foundLocationId = loc.id;
          foundLocationName = loc.name;
          break;
        }
      }

      // Always clean up URL parameters once locationMenuList has loaded,
      // regardless of whether the event was found — prevents stale params
      // from persisting if the event no longer exists.
      queryParams.delete("open_edit_drawer");
      queryParams.delete("event_insight_id");
      const newSearch = queryParams.toString();
      history.replace({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : "",
      });

      if (foundEvent && foundLocationId !== null) {
        setDrawerInitialStep(1); // Step 2 — returning from sensor selection
        setIsReturningFromSensorSelection(true);
        handleEditEvent(foundEvent, foundLocationId, foundLocationName);
      }
    }
  }, [
    location.search,
    locationMenuList,
    handleEditEvent,
    history,
    location.pathname,
  ]);

  // Handle edit drawer close
  const handleEditDrawerClose = useCallback(() => {
    onEditDrawerClose();
    setEventToEdit(null);
    setEditEventLocationId(null);
    setEditEventLocationName("");
    setIsReturningFromSensorSelection(false);
    setDrawerInitialStep(0);
  }, [onEditDrawerClose]);

  // Handle event updated
  const handleEventUpdated = useCallback(() => {
    refreshData();
    // Update Gantt chart
    oceanWeatherIntelligenceStore.setChangeForUpdate({
      update: true,
      expiration: 0,
    });
  }, [refreshData, oceanWeatherIntelligenceStore]);

  // Handle edit location request
  const handleEditLocation = useCallback(
    (location: ILocationMenuItem) => {
      setLocationToEdit(location);
      onLocationEditDrawerOpen();
    },
    [onLocationEditDrawerOpen],
  );

  // Handle location edit drawer close
  const handleLocationEditDrawerClose = useCallback(() => {
    onLocationEditDrawerClose();
    setLocationToEdit(null);
    // Clear the store data
    oceanWeatherIntelligenceStore.setSelectedGeofence([]);
    insightAlertStore.setSelectedStationForAlert(null);
  }, [
    onLocationEditDrawerClose,
    oceanWeatherIntelligenceStore,
    insightAlertStore,
  ]);

  // Handle location update submit
  const handleLocationUpdateSubmit = useCallback(
    async (values: {
      name: string;
      coordinates: { lat: number; lng: number }[];
      locationType?: "point" | "area" | null;
      regionId?: number;
      stationId?: number;
      macroRegionId?: number;
      savedLocationId?: number;
    }) => {
      console.log(
        "[EventManagerTab] handleLocationUpdateSubmit - received values:",
        JSON.stringify(values, null, 2),
      );
      if (!locationToEdit) return;

      setIsLocationUpdating(true);

      try {
        // Use the new coordinates from form if provided, otherwise fall back to original
        const geofenceToSave =
          values.coordinates.length > 0
            ? values.coordinates
            : locationToEdit.geofence;

        // Determine if this is a point (station) or area update
        const isPointType = values.locationType === "point";
        const hasStationData =
          isPointType && values.stationId && values.regionId;
        console.log(
          "[EventManagerTab] isPointType:",
          isPointType,
          "hasStationData:",
          hasStationData,
          "stationId:",
          values.stationId,
          "regionId:",
          values.regionId,
        );

        // When location type changed to area or coordinates were drawn on map,
        // clear station data. When it's a point with station data, keep the station.
        const regionIdToSave = hasStationData ? values.regionId : undefined;
        const stationIdToSave = hasStationData ? values.stationId : undefined;
        const macroRegionIdToSave = hasStationData
          ? values.macroRegionId
          : undefined;

        // Use savedLocationId when it's a saved area
        const locationIdToSave = values.savedLocationId;

        const success =
          await operationRuleLocationStore.updateOperationRuleLocationById(
            locationToEdit.id,
            values.name,
            "", // description
            regionIdToSave,
            stationIdToSave,
            macroRegionIdToSave,
            locationIdToSave,
            geofenceToSave,
            selectedDashboardId,
          );

        if (success) {
          toast({
            title: t("editLocationSuccess"),
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          refreshData();
          // Update Gantt chart to reflect location changes
          oceanWeatherIntelligenceStore.setChangeForUpdate({
            update: true,
            expiration: 0,
          });
          handleLocationEditDrawerClose();
        } else {
          toast({
            title: t("editLocationError"),
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error updating location:", error);
        toast({
          title: t("editLocationError"),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLocationUpdating(false);
      }
    },
    [
      locationToEdit,
      operationRuleLocationStore,
      selectedDashboardId,
      toast,
      t,
      refreshData,
      oceanWeatherIntelligenceStore,
      handleLocationEditDrawerClose,
    ],
  );

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteItem) return;

    setIsDeleting(true);

    try {
      if (deleteItem.type === "location") {
        await operationRuleLocationStore.deleteOperationRuleLocation(
          deleteItem.id,
        );
        // Remove location locally instead of refreshing from API
        removeLocation(deleteItem.id);
        // Update Gantt chart
        oceanWeatherIntelligenceStore.setChangeForUpdate({
          update: true,
          expiration: 0,
        });
        toast({
          title: t("deleteLocationSuccess"),
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        await operationRuleLocationInsightStore.deleteOperationRuleLocationInsight(
          deleteItem.id,
        );
        // Remove event locally instead of refreshing from API
        if (deleteItem.locationId) {
          removeEvent(deleteItem.locationId, deleteItem.id);
        }
        // Update Gantt chart
        oceanWeatherIntelligenceStore.setChangeForUpdate({
          update: true,
          expiration: 0,
        });
        toast({
          title: t("deleteEventSuccess"),
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }

      onDeleteModalClose();
      setDeleteItem(null);
    } catch (error) {
      console.error("Error deleting item:", error);
      toast({
        title:
          deleteItem.type === "location"
            ? t("deleteLocationError")
            : t("deleteEventError"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  }, [
    deleteItem,
    operationRuleLocationStore,
    operationRuleLocationInsightStore,
    oceanWeatherIntelligenceStore,
    toast,
    t,
    removeLocation,
    removeEvent,
    onDeleteModalClose,
  ]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    onDeleteModalClose();
    setDeleteItem(null);
  }, [onDeleteModalClose]);

  return (
    <Box>
      {/* Error Alert */}
      {error && (
        <Alert status="error" mb={4} color="black">
          <AlertIcon />
          {error}
        </Alert>
      )}

      {/* Read-only User Alert */}
      {authStore.readOnlyUser && (
        <Alert bg="#bee3f8" status="info" mb={4} color="gray.700">
          <AlertIcon />
          {i18n.t("readingModePleaseContact")}
        </Alert>
      )}

      {/* Header Section - Following Figma Design (only show when dashboards exist) */}
      {operationRuleStore.operationRules.length > 0 && (
        <Flex
          alignItems="center"
          justifyContent="space-between"
          mb={6}
          w="100%"
        >
          {/* Left Side - Dashboard Selector */}
          <Flex alignItems="center" gap={8}>
            <DashboardSelect
              operationRules={operationRuleStore.operationRules ?? []}
              selectedId={
                selectedDashboardId > 0 ? selectedDashboardId : undefined
              }
              onChange={handleSelectDashboard}
              onPinToggle={handlePinToggle}
              onEdit={handleEditDashboard}
              onDelete={handleDeleteDashboard}
              showActions={true}
              readOnly={authStore.readOnlyUser}
              pinLoadingId={pinLoadingId}
              hideIcon={true}
            />

            {/* Action buttons: "+", Edit, Delete */}
            {!authStore.readOnlyUser && (
              <Flex align="center" gap={2}>
                {/* "+" button to create new dashboard */}
                <Tooltip label={t("createDashboard")} placement="top" hasArrow>
                  <IconButton
                    aria-label={t("createDashboard")}
                    icon={<Icon as={Plus} boxSize={5} />}
                    layerStyle="lightOcean"
                    _hover={{ layerStyle: "lightOcean" }}
                    borderRadius="6px"
                    size="md"
                    minW="36px"
                    h="36px"
                    onClick={onCreateDashboardOpen}
                  />
                </Tooltip>

                {/* Edit and Delete buttons grouped together */}
                {selectedDashboard && (
                  <Flex align="center" gap={1}>
                    {/* Edit button for selected dashboard */}
                    <Tooltip
                      label={t("editDashboard")}
                      placement="top"
                      hasArrow
                    >
                      <IconButton
                        aria-label={t("editDashboard")}
                        icon={<Icon as={Pencil} boxSize={5} />}
                        bg="#e8ecf0"
                        color="#242e38"
                        borderRadius="8px"
                        size="md"
                        minW="36px"
                        h="36px"
                        _hover={{ bg: "#d8dde2" }}
                        _active={{ bg: "#c8cdd2" }}
                        onClick={() => handleEditDashboard(selectedDashboard)}
                      />
                    </Tooltip>

                    {/* Delete button for selected dashboard */}
                    <Tooltip
                      label={t("deleteDashboard")}
                      placement="top"
                      hasArrow
                    >
                      <IconButton
                        aria-label={t("deleteDashboard")}
                        icon={<Icon as={Trash2} boxSize={5} />}
                        bg="#e8ecf0"
                        color="#242e38"
                        borderRadius="8px"
                        size="md"
                        minW="36px"
                        h="36px"
                        _hover={{ bg: "#d8dde2" }}
                        _active={{ bg: "#c8cdd2" }}
                        onClick={() => handleDeleteDashboard(selectedDashboard)}
                      />
                    </Tooltip>
                  </Flex>
                )}
              </Flex>
            )}

            {/* Vertical Divider */}
            <Box w="1px" h="50px" bg="gray.400" />
          </Flex>

          {/* Right Side - Action Buttons */}
          <Flex alignItems="center" gap={4}>
            {/* Reorder Location Controls */}
            {!authStore.readOnlyUser && locationMenuList.length > 1 && (
              <Box
                w={isLocationDragMode ? "auto" : "44px"}
                display="flex"
                alignItems="center"
                gap={2}
              >
                {!isLocationDragMode ? (
                  <Tooltip label={t("reorderLocations")}>
                    <IconButton
                      aria-label={t("reorderLocations")}
                      icon={
                        <Icon
                          as={ArrowDownUp}
                          boxSize={4}
                          color="newBlue.500"
                        />
                      }
                      variant="ghost"
                      bg="blue.50"
                      w="44px"
                      h="44px"
                      borderRadius="8px"
                      _hover={{ bg: "#b6d8f0" }}
                      onClick={handleToggleLocationDragMode}
                    />
                  </Tooltip>
                ) : (
                  <Flex alignItems="center" gap={2}>
                    <Tooltip label={t("saveOrder")}>
                      <IconButton
                        aria-label={t("saveOrder")}
                        icon={<Icon as={Check} boxSize={4} />}
                        size="sm"
                        color="newBlue.500"
                        variant="ghost"
                        bg="blue.50"
                        _hover={{ bg: "#b6d8f0" }}
                        onClick={handleSaveLocationOrder}
                      />
                    </Tooltip>
                    <Tooltip label={t("cancelReorder")}>
                      <IconButton
                        aria-label={t("cancelReorder")}
                        icon={<Icon as={X} boxSize={4} />}
                        size="sm"
                        color="gray.500"
                        variant="ghost"
                        bg="gray.100"
                        _hover={{ bg: "gray.200" }}
                        onClick={handleCancelLocationOrder}
                      />
                    </Tooltip>
                  </Flex>
                )}
              </Box>
            )}

            {/* New Event Button */}
            {!authStore.readOnlyUser && (
              <Button
                bg="newBlue.500"
                color="white"
                h="44px"
                px={5}
                borderRadius="8px"
                _hover={{ bg: "newBlue.600" }}
                leftIcon={<Icon as={Plus} boxSize={4} />}
                onClick={onEventDrawerOpen}
              >
                <Text fontSize="16px" fontWeight="500">
                  {t("newEvent")}
                </Text>
              </Button>
            )}
          </Flex>
        </Flex>
      )}

      {/* Loading State */}
      {isCombinedLoading && !isEventDrawerOpen && <Loading />}

      {/* Empty State - No Dashboards */}
      {!isCombinedLoading &&
        !isEventDrawerOpen &&
        operationRuleStore.operationRules.length === 0 && (
          <EmptyState
            image={noInsights}
            description={t("selectDashboard")}
            imageSize="20vw"
            marginTop="15vh"
            buttonAction={onCreateDashboardOpen}
            buttonIcon={<Icon as={Plus} boxSize={4} />}
            buttonText={
              !authStore.readOnlyUser
                ? (t("createDashboard") as string)
                : undefined
            }
          />
        )}

      {/* Empty State - No Locations */}
      {!isCombinedLoading &&
        !isEventDrawerOpen &&
        operationRuleStore.operationRules.length > 0 &&
        locationMenuList.length === 0 && (
          <EmptyState
            image={noInsights}
            description={t("noLocations")}
            imageSize="20vw"
            marginTop="15vh"
            buttonAction={onEventDrawerOpen}
            buttonIcon={<Icon as={Plus} boxSize={4} />}
            buttonText={
              !authStore.readOnlyUser ? (t("newEvent") as string) : undefined
            }
          />
        )}

      {/* Location Accordion with Events */}
      {!isCombinedLoading &&
        operationRuleStore.operationRules.length > 0 &&
        locationMenuList.length > 0 && (
          <LocationAccordion
            dashboardId={selectedDashboardId}
            locationMenuList={locationMenuList}
            disableDragAndDrop={authStore.readOnlyUser}
            onEventReorder={handleEventReorder}
            isLocationDragMode={isLocationDragMode}
            locationSaveKey={locationSaveKey}
            onLocationOrderChange={handleLocationOrderChange}
            onDeleteLocation={handleDeleteLocation}
            onDeleteEvent={handleDeleteEvent}
            onEditEvent={handleEditEvent}
            onCreateEventForLocation={handleCreateEventForLocation}
            onEditLocation={handleEditLocation}
          />
        )}

      {/* Event Create Drawer */}
      {isEventDrawerOpen && (
        <EventCreateDrawer
          isOpen={isEventDrawerOpen}
          onClose={() => {
            setDrawerInitialStep(0);
            handleEventDrawerClose();
          }}
          showPhoneVerificationModal={!isPhoneAlreadyVerified}
          onPhoneModalOpen={onPhoneModalOpen}
          onEventCreated={handleEventCreated}
          prefilledLocation={prefilledLocation}
          isLocationLocked={!!prefilledLocation}
          prefilledDashboardId={selectedDashboardId}
          forceInsightEnabled={true}
          initialStep={drawerInitialStep}
        />
      )}

      {/* Event Edit Drawer */}
      {isEditDrawerOpen && eventToEdit && (
        <EventEditDrawer
          isOpen={isEditDrawerOpen}
          onClose={handleEditDrawerClose}
          eventToEdit={eventToEdit}
          locationId={editEventLocationId}
          locationName={editEventLocationName}
          dashboardId={selectedDashboardId}
          onEventUpdated={handleEventUpdated}
          isReturningFromSensorSelection={isReturningFromSensorSelection}
        />
      )}

      {/* Location Edit Drawer */}
      {isLocationEditDrawerOpen && locationToEdit && (
        <LocationEditDrawer
          isOpen={isLocationEditDrawerOpen}
          onClose={handleLocationEditDrawerClose}
          onSubmit={handleLocationUpdateSubmit}
          location={locationToEdit}
          isLoading={isLocationUpdating}
        />
      )}

      {/* Phone Verification Modal */}
      <PhoneVerificationModal
        isOpen={isPhoneModalOpen}
        onClose={onPhoneModalClose}
        onSuccess={handlePhoneUpdateSuccess}
        initialPhoneData={phoneData}
        title={tPhoneModal("updatePhoneNumber") as string}
        description={tPhoneModal("verifyPhoneToReceiveAlerts") as string}
        confirmButtonText={tPhoneModal("sendVerificationCode") as string}
        isPhoneAlreadyVerified={isPhoneAlreadyVerified}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        type={deleteItem?.type || "event"}
        itemName={deleteItem?.name || ""}
        isLoading={isDeleting}
        t={t}
      />

      {/* Modal for creating new dashboard */}
      <CreateDashboardModal
        isOpen={isCreateDashboardOpen}
        onClose={onCreateDashboardClose}
        onDashboardCreated={handleDashboardCreated}
        t={t}
      />

      {/* Modal for editing dashboard */}
      {dashboardToEdit && (
        <DashboardModal
          isOpen={isEditDashboardOpen}
          onClose={() => {
            onEditDashboardClose();
            setDashboardToEdit(null);
          }}
          onConfirm={handleSaveEditDashboard}
          title={`${t("editDashboardTitle")}`}
          cancelButtonText={`${t("goBack")}`}
          confirmButtonText={
            isDashboardUpdating ? `${t("processing")}` : `${t("save")}`
          }
          initialRef={editDashboardInputRef}
          nameFieldName="editDashboardName"
          nameFieldLabel={`${t("dashboardName")}`}
          nameFieldPlaceholder={`${t("dashboardNamePlaceholder")}`}
          nameFieldMaxLength={50}
          formik={formikEditDashboard}
          initialValues={formikEditDashboard.values}
        />
      )}

      {/* Modal for deleting dashboard */}
      <DeleteDashboardModal
        isOpen={isDeleteDashboardOpen}
        onClose={handleCancelDeleteDashboard}
        onConfirm={handleConfirmDeleteDashboard}
        dashboardName={dashboardToDelete?.name || ""}
        isLoading={isDashboardDeleting}
        t={t}
      />
    </Box>
  );
});

export default EventManagerTab;
